import React, { useRef } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useTranslation } from 'react-i18next';
import { useCountryConfig } from '../hooks/useCountryConfig';
import { getCurrencyTextLabel } from '../utils/currencyUtils';

interface InvoiceItem {
  name: string;
  quantity: number;
  price: number;
}

interface CleanThermalInvoiceProps {
  orderNumber: string;
  date: string;
  time: string;
  taxNumber: string;
  phoneNumber: string;
  paymentMethod: string;
  items: InvoiceItem[];
  subtotal: number;
  tax: number;
  total: number;
}

const CleanThermalInvoice: React.FC<CleanThermalInvoiceProps> = ({
  orderNumber,
  date,
  time,
  taxNumber,
  phoneNumber,
  paymentMethod,
  items,
  subtotal,
  tax,
  total
}) => {
  const invoiceRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const countryConfig = useCountryConfig();
  const currencyLabel = getCurrencyTextLabel(
    countryConfig,
    t('currency.egyptSymbol'),
    'ريال'
  );

  const downloadPDF = async () => {
    if (!invoiceRef.current) {
      alert('خطأ: لم يتم العثور على الفاتورة');
      return;
    }

    try {
      console.log('بدء عملية التحميل...');
      
      // تحويل HTML إلى صورة باستخدام html2canvas
      const canvas = await html2canvas(invoiceRef.current, {
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: true,
        allowTaint: true,
        scale: 3,
        height: invoiceRef.current.scrollHeight,
        width: invoiceRef.current.scrollWidth,
      } as any);

      console.log('تم إنشاء Canvas بنجاح');

      // إنشاء PDF جديد
      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [80, 120], // حجم الإيصال الحراري
      });

      // حساب الأبعاد
      const pdfWidth = 80;
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      // إضافة الصورة للـ PDF
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

      console.log('تم إنشاء PDF بنجاح');

      // تحميل الملف مباشرة
      pdf.save(`invoice-${orderNumber}.pdf`);
      
      console.log('تم تحميل الملف بنجاح');
    } catch (error) {
      console.error('خطأ في تحميل الفاتورة:', error);
      alert('حدث خطأ أثناء تحميل الفاتورة. يرجى المحاولة مرة أخرى.');
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fdf6] p-4 flex items-center justify-center" dir="rtl">
      <div className="w-full max-w-sm">
        {/* الفاتورة */}
        <div
          ref={invoiceRef}
          className="bg-white rounded-lg shadow-lg p-4 mb-6"
          style={{ fontFamily: 'Arial, sans-serif' }}
        >
          {/* الهيدر - التاريخ والوقت */}
          <div className="text-center text-xs text-gray-500 mb-3">
            {date} • {time}
          </div>

          {/* عنوان الفاتورة */}
          <div className="text-center mb-4">
            <h1 className="text-lg font-bold mb-3">فاتورة الطلب</h1>
            
            {/* رقم الطلب */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 mb-4">
              <div className="text-2xl font-bold text-gray-700">{orderNumber}</div>
            </div>
          </div>

          {/* بيانات العميل - بدون عنوان */}
          <div className="mb-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="font-bold text-gray-700">الرقم الضريبي:</span>
              <span className="text-gray-600">{taxNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-bold text-gray-700">رقم هاتف العميل:</span>
              <span className="text-gray-600">{phoneNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-bold text-gray-700">طريقة الدفع:</span>
              <span className="text-gray-600">{paymentMethod}</span>
            </div>
          </div>

          {/* جدول المنتجات - بدون عنوان */}
          <div className="mb-4">
            {/* رأس الجدول */}
            <div className="border-t border-b border-gray-300 py-2 mb-2">
              <div className="grid grid-cols-3 gap-2 text-sm font-bold text-gray-700">
                <div className="text-right">المنتج</div>
                <div className="text-center">الكمية</div>
                <div className="text-left">السعر</div>
              </div>
            </div>

            {/* صفوف المنتجات */}
            {items.map((item, index) => (
              <div key={index} className="border-b border-gray-100 py-2">
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="text-right text-gray-800">{item.name}</div>
                  <div className="text-center text-gray-800">{item.quantity}</div>
                  <div className="text-left text-gray-800">{item.price.toFixed(1)} {currencyLabel}</div>
                </div>
              </div>
            ))}
          </div>

          {/* الملخص */}
          <div className="space-y-2 mb-4 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-700">المجموع الفرعي:</span>
              <span className="text-gray-600">{subtotal.toFixed(1)} {currencyLabel}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-700">الضريبة %15:</span>
              <span className="text-gray-600">{tax.toFixed(1)} {currencyLabel}</span>
            </div>
            <div className="border-t border-gray-300 pt-2 mt-3">
              <div className="flex justify-between text-base font-bold">
                <span className="text-gray-800">الإجمالي:</span>
                <span className="text-gray-800">{total.toFixed(1)} {currencyLabel}</span>
              </div>
            </div>
          </div>

          {/* الفوتر - ملاحظة */}
          <div className="text-center text-xs text-gray-400 leading-relaxed">
            <p>ملاحظة: الفاتورة الضريبية سيتم إرسال الطلب من متجر</p>
            <p>استرجاع مبلغ الضريبة عند طلب فاتورة ضريبية في حال عدم استلام الطلب</p>
          </div>
        </div>

        {/* زر التحميل */}
        <button
          onClick={downloadPDF}
          className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 shadow-md"
        >
          تحميل الفاتورة PDF
        </button>
      </div>
    </div>
  );
};

export default CleanThermalInvoice;
